import { useNavigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import { noop, sortBy } from 'lodash';
import { PluviometroFormValues } from '../types';
import useAsyncFetch from '../../../hooks/use-async';
import PluviometrosService, {
  PluviometroToSave,
} from '../../../services/pluviometros';
import Agro1Loader from '../../../components/Agro1Loader';
import Agro1Toast from '../../../components/Agro1Toast';
import { beforeExit } from '../../utils';
import {
  PluviometroForm,
  defaultValues,
  validationSchema,
} from './form-controller';
import { PropriedadesService } from '../../../services';
import useLocalStore from '../../../hooks/use-local-store';
import { useForm } from '../../../components/Form/hooks';
import { getMessagesPluviometros } from '../utils';

const PluviometroCreateEditPage = () => {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const formObject = useForm<PluviometroFormValues>({
    validationSchema,
    defaultValues,
  });
  const { reset, handleSubmit, getValues, setValue } = formObject.form;
  const currentPropriedade = useLocalStore('currentPropriedade').value;

  const exit = () => {
    navigate('/gerenciador-dados/pluviometros');
  };

  const { data: pluviometro } = useAsyncFetch(
    {
      withLoader: true,
      callback: async () => {
        if (params.id) return PluviometrosService.find(params.id);
      },
      errorMessage: 'Falha ao carregar o registro para edição.',
      onError: exit,
    },
    [params.id],
  );

  const { data: propriedades } = useAsyncFetch(
    {
      callback: async () =>
        sortBy(await PropriedadesService.all(), 'descricao'),
      errorMessage: 'Falha ao carregar propriedades.',
      withLoader: true,
    },
    [],
  );

  useEffect(() => {
    const novaPropriedadeId =
      pluviometro?.cdPropriedade || currentPropriedade?.id;

    if (!propriedades || !novaPropriedadeId) return;

    const propriedade = propriedades.find(({ id }) => novaPropriedadeId === id);

    setValue('propriedade', propriedade);
  });

  useEffect(() => {
    if (!pluviometro) return;

    reset({
      ...getValues(),
      id: pluviometro.id,
      descricao: pluviometro.descricao,
    });
  }, [pluviometro, reset, getValues]);

  const onSubmit = async () => {
    const formValue = getValues();
    const loaderId = Agro1Loader.show();

    try {
      const record: PluviometroToSave = {
        descricao: formValue.descricao!.toUpperCase(),
        cdPropriedade: formValue.propriedade!.id,
        cdProdutor: formValue.propriedade!.produtor.id,
      };

      if (params.id) {
        await PluviometrosService.update({ ...record, id: params.id });
      } else {
        await PluviometrosService.create(record);
      }

      exit();

      Agro1Toast.show({
        detail: 'Registro salvo com sucesso.',
        severity: 'success',
      });
    } catch (error) {
      Agro1Toast.show({
        detail: getMessagesPluviometros(error, params.id ? 'edit' : 'create'),
        severity: 'error',
        sticky: true,
      });
    } finally {
      Agro1Loader.close(loaderId);
    }
  };

  return (
    <div className="container w-full gerenciador-dados-create-edit-container">
      <div className="flex flex-wrap mt-6 mx-3">
        <div className="text-3xl font-normal">
          {pluviometro ? 'Editar' : 'Novo'} Pluviômetro
        </div>
      </div>
      <ConfirmDialog />
      <div>
        <Panel className="mt-4 mx-3">
          <div className="col-12 m-auto">
            <PluviometroForm
              formObject={formObject}
              submitByEnter={false}
              onSubmit={noop}
            >
              <div className="col-12 flex align-items-center pb-0">
                <PluviometroForm.InputTextarea
                  name="descricao"
                  label="Descrição:"
                  containerClassName="col-6"
                  className="w-full"
                  rows={1}
                />
                <PluviometroForm.Dropdown
                  name="propriedade"
                  label="Propriedade:"
                  options={propriedades}
                  dataKey="id"
                  optionLabel="descricao"
                  containerClassName="col-6"
                  disabled={!!pluviometro}
                />
              </div>
            </PluviometroForm>
          </div>
        </Panel>
        <div className="flex justify-content-end mt-2 mx-3">
          <Button
            type="button"
            className="button-danger mr-1"
            label="Cancelar"
            onClick={() => beforeExit({ onConfirm: exit })}
          />
          <Button
            type="submit"
            className="button-secondary"
            label="Salvar"
            onClick={() => handleSubmit(onSubmit)()}
          />
        </div>
      </div>
    </div>
  );
};

export default PluviometroCreateEditPage;
